import './editor.css';

export type EditorTool = 'pan' | 'point' | 'rectangle' | 'arrow' | 'text' | 'polygon';
export interface EditorAnnotation {
  id: string;
  type: 'POINT' | 'RECTANGLE' | 'POLYGON' | 'ARROW' | 'TEXT';
  points: [number, number][];
  text?: string;
  color?: string;
}
export interface EditorOptions {
  onAnnotation: (shape: Omit<EditorAnnotation, 'id'>) => void;
  onSelect: (id: string | null) => void;
  onUpdate?: (shape: EditorAnnotation) => void;
  onDelete?: (id: string) => void;
  onError?: (message: string) => void;
}
const NS = 'http://www.w3.org/2000/svg';
const labels: Record<EditorTool, string> = { pan: 'Flytt / velg', point: 'Punkt', rectangle: 'Ramme', arrow: 'Pil', text: 'Tekst', polygon: 'Område' };
const icons: Record<EditorTool, string> = {
  pan: '<path d="m5 3 11 8-6 1-2 6z"/>', point: '<circle cx="10" cy="10" r="4"/><path d="M10 1v3m0 12v3M1 10h3m12 0h3"/>',
  rectangle: '<rect x="3" y="4" width="14" height="12" rx="1"/>', arrow: '<path d="M3 17 17 3M7 3h10v10"/>',
  text: '<path d="M3 4h14M10 4v13M7 17h6"/>', polygon: '<path d="m3 5 12-2 2 12-10 3-4-6z"/>'
};
const clamp = (n: number) => Math.max(0, Math.min(1, n));

/** Original images remain unchanged. Every saved annotation uses normalized image coordinates. */
export class ImageEditor {
  get isImageLoaded(): boolean { return this.loaded; }
  readonly root: HTMLElement;
  private readonly stage: HTMLElement;
  private readonly image: HTMLImageElement;
  private readonly overlay: SVGSVGElement;
  private readonly message: HTMLElement;
  private readonly hint: HTMLElement;
  private readonly textInput: HTMLInputElement;
  private readonly finishButton: HTMLButtonElement;
  private readonly deleteButton: HTMLButtonElement;
  private readonly observer: ResizeObserver;
  private readonly abort = new AbortController();
  private annotations: EditorAnnotation[] = [];
  private selected: string | null = null;
  private tool: EditorTool = 'pan';
  private points: [number, number][] = [];
  private preview: [number, number] | null = null;
  private width = 0;
  private height = 0;
  private scale = 1;
  private x = 0;
  private y = 0;
  private loading = false;
  private loaded = false;
  private generation = 0;
  private source = '';
  private alt = '';
  private dragging: { startX: number; startY: number; x: number; y: number; point: [number, number] | null; annotation?: EditorAnnotation } | null = null;

  constructor(host: HTMLElement, private readonly options: EditorOptions) {
    this.root = document.createElement('section');
    this.root.className = 'tc-editor';
    this.root.setAttribute('aria-label', 'Bildekontroll og markering');
    const toolbar = document.createElement('div');
    toolbar.className = 'tc-editor-toolbar';
    toolbar.setAttribute('role', 'toolbar');
    toolbar.setAttribute('aria-label', 'Bildeverktøy');
    for (const tool of Object.keys(labels) as EditorTool[]) {
      const button = this.button(labels[tool], () => this.setTool(tool));
      button.dataset.tool = tool;
      button.innerHTML = `<svg viewBox="0 0 20 20" aria-hidden="true">${icons[tool]}</svg><span>${labels[tool]}</span>`;
      toolbar.append(button);
    }
    this.finishButton = this.button('Fullfør område', () => this.finishPolygon());
    this.finishButton.hidden = true;
    toolbar.append(this.finishButton);
    this.deleteButton = this.button('Slett valgt', () => this.removeSelected());
    this.deleteButton.disabled = true;
    toolbar.append(this.deleteButton);
    this.textInput = document.createElement('input');
    this.textInput.type = 'text';
    this.textInput.maxLength = 180;
    this.textInput.placeholder = 'Skriv tekst, klikk der den skal plasseres';
    this.textInput.setAttribute('aria-label', 'Tekst til markering');
    this.textInput.className = 'tc-editor-text';
    this.textInput.hidden = true;
    this.stage = document.createElement('div');
    this.stage.className = 'tc-editor-stage';
    this.stage.tabIndex = 0;
    this.stage.setAttribute('aria-label', 'Bilde. Rull for zoom. Escape avbryter. 0 tilpasser bildet.');
    this.image = document.createElement('img');
    this.image.draggable = false;
    this.image.hidden = true;
    this.overlay = document.createElementNS(NS, 'svg');
    this.overlay.classList.add('tc-editor-overlay');
    this.overlay.setAttribute('aria-label', 'Bildemarkeringer');
    this.message = document.createElement('div');
    this.message.className = 'tc-editor-message';
    this.message.setAttribute('role', 'status');
    this.message.textContent = 'Velg et bilde for å starte kontrollen';
    this.stage.append(this.image, this.overlay, this.message);
    const footer = document.createElement('div');
    footer.className = 'tc-editor-footer';
    this.hint = document.createElement('span');
    this.hint.className = 'tc-editor-hint';
    const controls = document.createElement('div');
    controls.className = 'tc-editor-zoom';
    controls.append(this.button('Zoom ut', () => this.zoom(-1)), this.button('Tilpass', () => this.fit()), this.button('Zoom inn', () => this.zoom(1)), this.button('Fullskjerm', () => this.fullscreen()));
    footer.append(this.hint, controls);
    toolbar.append(this.textInput);
    this.root.append(toolbar, this.stage, footer);
    host.append(this.root);
    const signal = this.abort.signal;
    this.stage.addEventListener('pointerdown', this.pointerDown, { signal });
    this.stage.addEventListener('pointermove', this.pointerMove, { signal });
    this.stage.addEventListener('pointerup', this.pointerUp, { signal });
    this.stage.addEventListener('pointercancel', this.cancelDrag, { signal });
    this.stage.addEventListener('wheel', this.wheel, { signal, passive: false });
    this.root.addEventListener('keydown', this.keyDown, { signal });
    this.observer = new ResizeObserver(() => { if (this.loaded) this.fit(); });
    this.observer.observe(this.stage);
    this.setTool('pan');
  }

  private button(label: string, action: () => void): HTMLButtonElement {
    const button = document.createElement('button');
    button.type = 'button';
    button.textContent = label;
    button.title = label;
    button.setAttribute('aria-label', label);
    button.addEventListener('click', action, { signal: this.abort.signal });
    return button;
  }

  async load(url: string, alt: string, annotations: EditorAnnotation[] = []): Promise<void> {
    const generation = ++this.generation;
    this.source = url;
    this.alt = alt;
    this.annotations = annotations;
    this.selected = null;
    this.deleteButton.disabled = true;
    this.points = [];
    this.preview = null;
    this.dragging = null;
    this.loaded = false;
    this.loading = Boolean(url);
    this.image.hidden = true;
    this.overlay.replaceChildren();
    this.message.hidden = false;
    this.message.replaceChildren();
    this.message.textContent = url ? 'Laster bilde …' : 'Ingen bilde valgt';
    this.stage.classList.toggle('is-loading', Boolean(url));
    if (!url) { this.updateHint(); return; }
    const pending = new Image();
    pending.src = url;
    try {
      await pending.decode();
      if (generation !== this.generation) return;
      this.width = pending.naturalWidth;
      this.height = pending.naturalHeight;
      if (!this.width || !this.height) throw new Error('Empty image');
      this.image.src = url;
      this.image.alt = alt;
      this.image.hidden = false;
      this.loaded = true;
      this.loading = false;
      this.message.hidden = true;
      this.stage.classList.remove('is-loading');
      this.fit();
    } catch {
      if (generation !== this.generation) return;
      this.loading = false;
      this.stage.classList.remove('is-loading');
      const error = 'Bildet kunne ikke lastes. Kontroller tilkoblingen og prøv igjen.';
      this.message.textContent = error;
      this.message.append(this.button('Prøv igjen', () => { void this.load(this.source, this.alt, this.annotations); }));
      this.options.onError?.(error);
      this.updateHint();
    }
  }

  setAnnotations(annotations: EditorAnnotation[]): void {
    this.annotations = annotations;
    if (!annotations.some(a => a.id === this.selected)) this.selected = null;
    this.deleteButton.disabled = !this.selected || !this.options.onDelete;
    this.render();
  }
  setTool(tool: EditorTool): void {
    this.tool = tool;
    this.points = [];
    this.preview = null;
    this.dragging = null;
    this.textInput.hidden = tool !== 'text';
    this.finishButton.hidden = tool !== 'polygon';
    this.root.querySelectorAll<HTMLButtonElement>('[data-tool]').forEach(b => b.setAttribute('aria-pressed', String(b.dataset.tool === tool)));
    this.updateHint();
    this.render();
  }
  fit(): void {
    if (!this.loaded) return;
    this.scale = Math.min(this.stage.clientWidth / this.width, this.stage.clientHeight / this.height);
    this.x = (this.stage.clientWidth - this.width * this.scale) / 2;
    this.y = (this.stage.clientHeight - this.height * this.scale) / 2;
    this.render();
  }
  zoom(delta: number): void { this.zoomAt(Math.pow(1.2, delta), this.stage.clientWidth / 2, this.stage.clientHeight / 2); }
  private zoomAt(factor: number, px: number, py: number): void {
    if (!this.loaded) return;
    const fit = Math.min(this.stage.clientWidth / this.width, this.stage.clientHeight / this.height);
    const next = Math.max(fit * 0.4, Math.min(fit * 24, this.scale * factor));
    this.x = px - (px - this.x) * next / this.scale;
    this.y = py - (py - this.y) * next / this.scale;
    this.scale = next;
    this.render();
  }
  removeSelected(): void { if (this.selected) this.options.onDelete?.(this.selected); }
  undo(): void { this.points.pop(); this.preview = null; this.render(); this.updateHint(); }
  private position(event: PointerEvent): [number, number] | null {
    const rect = this.stage.getBoundingClientRect();
    const px = (event.clientX - rect.left - this.x) / (this.width * this.scale);
    const py = (event.clientY - rect.top - this.y) / (this.height * this.scale);
    return px >= 0 && px <= 1 && py >= 0 && py <= 1 ? [px, py] : null;
  }
  private select(id: string | null): void {
    this.selected = id;
    this.deleteButton.disabled = !id || !this.options.onDelete;
    this.options.onSelect(id);
    this.render();
  }
  private pointerDown = (event: PointerEvent): void => {
    if (!this.loaded || event.button !== 0) return;
    event.preventDefault();
    this.stage.focus({ preventScroll: true });
    const point = this.position(event);
    const mark = (event.target as Element).closest('[data-annotation]');
    if (this.tool === 'pan' || event.shiftKey) {
      const annotation = mark ? this.annotations.find(a => a.id === mark.getAttribute('data-annotation')) : undefined;
      this.select(annotation?.id ?? null);
      this.dragging = { startX: event.clientX, startY: event.clientY, x: this.x, y: this.y, point, annotation: annotation && this.options.onUpdate ? { ...annotation, points: annotation.points.map(p => [...p]) } : undefined };
      this.stage.setPointerCapture(event.pointerId);
      return;
    }
    if (!point) return;
    if (this.tool === 'point') this.emit('POINT', [point]);
    else if (this.tool === 'text') {
      const text = this.textInput.value.trim();
      if (!text) { this.hint.textContent = 'Skriv teksten før du plasserer den på bildet.'; this.textInput.focus(); return; }
      this.emit('TEXT', [point], text);
    } else if (this.tool === 'polygon') {
      this.points.push(point);
      this.preview = null;
      this.render(); this.updateHint();
    } else {
      this.points = [point];
      this.preview = point;
      this.dragging = { startX: event.clientX, startY: event.clientY, x: this.x, y: this.y, point };
      this.stage.setPointerCapture(event.pointerId);
    }
  };
  private pointerMove = (event: PointerEvent): void => {
    if (!this.loaded) return;
    if (this.dragging) {
      if (this.dragging.annotation && this.dragging.point) {
        const dx = (event.clientX - this.dragging.startX) / (this.width * this.scale);
        const dy = (event.clientY - this.dragging.startY) / (this.height * this.scale);
        const old = this.dragging.annotation;
        const shiftX = Math.max(-Math.min(...old.points.map(p => p[0])), Math.min(1 - Math.max(...old.points.map(p => p[0])), dx));
        const shiftY = Math.max(-Math.min(...old.points.map(p => p[1])), Math.min(1 - Math.max(...old.points.map(p => p[1])), dy));
        this.annotations = this.annotations.map(a => a.id === old.id ? { ...a, points: old.points.map(p => [clamp(p[0] + shiftX), clamp(p[1] + shiftY)]) } : a);
      } else if (this.tool === 'pan' || event.shiftKey) {
        this.x = this.dragging.x + event.clientX - this.dragging.startX;
        this.y = this.dragging.y + event.clientY - this.dragging.startY;
      } else this.preview = this.position(event);
      this.render();
    } else if (this.tool === 'polygon' && this.points.length) { this.preview = this.position(event); this.render(); }
  };
  private pointerUp = (event: PointerEvent): void => {
    if (!this.dragging) return;
    const drag = this.dragging;
    this.dragging = null;
    if (this.stage.hasPointerCapture(event.pointerId)) this.stage.releasePointerCapture(event.pointerId);
    if (drag.annotation) {
      const changed = this.annotations.find(a => a.id === drag.annotation!.id);
      if (changed && JSON.stringify(changed.points) !== JSON.stringify(drag.annotation.points)) this.options.onUpdate?.(changed);
    } else if ((this.tool === 'rectangle' || this.tool === 'arrow') && this.points[0]) {
      const end = this.position(event) ?? this.preview;
      const start = this.points[0];
      if (end && Math.hypot((end[0] - start[0]) * this.width * this.scale, (end[1] - start[1]) * this.height * this.scale) > 5) this.emit(this.tool === 'rectangle' ? 'RECTANGLE' : 'ARROW', [start, end]);
      else this.hint.textContent = 'Dra mellom to forskjellige punkter på bildet.';
      this.points = []; this.preview = null; this.render();
    }
  };
  private cancelDrag = (): void => {
    if (this.dragging?.annotation) {
      const old = this.dragging.annotation;
      this.annotations = this.annotations.map(a => a.id === old.id ? old : a);
    }
    this.dragging = null; this.points = []; this.preview = null; this.render(); this.updateHint();
  };
  private emit(type: EditorAnnotation['type'], points: [number, number][], text?: string): void {
    this.options.onAnnotation({ type, points: points.map(p => [...p]), ...(text ? { text } : {}) });
    this.points = []; this.preview = null; this.render(); this.updateHint();
  }
  private finishPolygon(): void {
    if (this.points.length < 3) return;
    const area = this.points.reduce((sum, p, i) => { const q = this.points[(i + 1) % this.points.length]; return sum + p[0] * q[1] - q[0] * p[1]; }, 0);
    if (Math.abs(area) < 0.00001 || polygonCrossesItself(this.points)) { this.hint.textContent = 'Området må ha minst tre gyldige hjørner og kan ikke krysse seg selv.'; return; }
    this.emit('POLYGON', this.points);
  }
  private wheel = (event: WheelEvent): void => {
    if (!this.loaded) return;
    event.preventDefault();
    const rect = this.stage.getBoundingClientRect();
    this.zoomAt(Math.exp(-Math.max(-100, Math.min(100, event.deltaY)) * 0.003), event.clientX - rect.left, event.clientY - rect.top);
  };
  private keyDown = (event: KeyboardEvent): void => {
    if ((event.target as Element).matches('input,textarea,select')) return;
    if (event.key === 'Escape') { this.cancelDrag(); this.select(null); }
    else if (event.key === '0') this.fit();
    else if (event.key === 'Delete' || event.key === 'Backspace') { event.preventDefault(); this.removeSelected(); }
    else if (event.key === 'Enter' && this.tool === 'polygon') this.finishPolygon();
    else if (event.key === 'z' && (event.ctrlKey || event.metaKey)) { event.preventDefault(); this.undo(); }
  };
  private updateHint(): void {
    this.finishButton.disabled = this.points.length < 3;
    this.hint.textContent = !this.loaded ? (this.loading ? 'Laster originalbildet' : 'Velg et tilgjengelig bilde') : this.tool === 'pan' ? 'Dra for å flytte · rull for zoom · klikk en markering for å velge' : this.tool === 'polygon' ? `${this.points.length} hjørner · klikk for å tegne · Enter fullfører · Escape avbryter` : this.tool === 'rectangle' || this.tool === 'arrow' ? 'Dra på bildet for å markere · Escape avbryter' : this.tool === 'text' ? 'Skriv tekst og klikk på bildet' : 'Klikk på bildet for å legge til et punkt';
  }
  private render(): void {
    if (!this.loaded) return;
    const w = this.width * this.scale, h = this.height * this.scale;
    this.image.style.width = `${w}px`; this.image.style.height = `${h}px`;
    this.image.style.transform = `translate(${this.x}px, ${this.y}px)`;
    this.overlay.setAttribute('viewBox', `0 0 ${this.stage.clientWidth} ${this.stage.clientHeight}`);
    this.overlay.replaceChildren();
    const shape = (a: Omit<EditorAnnotation, 'id'> & { id?: string }, draft = false) => {
      const group = document.createElementNS(NS, 'g');
      const coords = a.points.map(p => [this.x + p[0] * w, this.y + p[1] * h]);
      if (!coords.length) return;
      const color = a.color || '#58c5a5';
      group.setAttribute('stroke', color); group.setAttribute('stroke-width', a.id === this.selected ? '3' : '2'); group.setAttribute('fill', 'none');
      if (draft) { group.setAttribute('stroke-dasharray', '6 4'); group.classList.add('is-draft'); }
      if (a.id) { group.dataset.annotation = a.id; group.setAttribute('role', 'button'); group.setAttribute('tabindex', '0'); group.setAttribute('aria-label', a.text || 'Velg bildemarkering'); group.addEventListener('keydown', e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.select(a.id!); } }); }
      const [px, py] = coords[0];
      const el = document.createElementNS(NS, a.type === 'RECTANGLE' ? 'rect' : a.type === 'POINT' ? 'circle' : a.type === 'TEXT' ? 'text' : a.type === 'POLYGON' ? 'polygon' : 'path');
      if (a.type === 'POINT') { el.setAttribute('cx', String(px)); el.setAttribute('cy', String(py)); el.setAttribute('r', '7'); el.setAttribute('fill', color); }
      else if (a.type === 'RECTANGLE' && coords[1]) { el.setAttribute('x', String(Math.min(px, coords[1][0]))); el.setAttribute('y', String(Math.min(py, coords[1][1]))); el.setAttribute('width', String(Math.abs(coords[1][0] - px))); el.setAttribute('height', String(Math.abs(coords[1][1] - py))); }
      else if (a.type === 'POLYGON') el.setAttribute('points', coords.map(p => p.join(',')).join(' '));
      else if (a.type === 'TEXT') { el.setAttribute('x', String(px)); el.setAttribute('y', String(py)); el.setAttribute('fill', color); el.setAttribute('stroke', '#152028'); el.setAttribute('stroke-width', '3'); el.setAttribute('paint-order', 'stroke'); el.setAttribute('font-size', '16'); el.setAttribute('font-weight', '600'); el.textContent = a.text || ''; }
      else if (coords[1]) {
        const [ex, ey] = coords[1]; const angle = Math.atan2(ey - py, ex - px); const size = 13;
        el.setAttribute('d', `M${px} ${py}L${ex} ${ey}M${ex - size * Math.cos(angle - .5)} ${ey - size * Math.sin(angle - .5)}L${ex} ${ey}L${ex - size * Math.cos(angle + .5)} ${ey - size * Math.sin(angle + .5)}`);
      }
      el.classList.add('tc-editor-mark');
      group.append(el);
      this.overlay.append(group);
    };
    for (const annotation of this.annotations) shape(annotation);
    if (this.points.length) shape({ type: this.tool === 'polygon' ? 'POLYGON' : this.tool === 'arrow' ? 'ARROW' : 'RECTANGLE', points: [...this.points, ...(this.preview ? [this.preview] : [])] }, true);
  }
  private async fullscreen(): Promise<void> {
    try { if (document.fullscreenElement === this.root) await document.exitFullscreen(); else await this.root.requestFullscreen(); }
    catch { this.options.onError?.('Fullskjerm er ikke tilgjengelig i denne nettleseren.'); }
  }
  dispose(): void { ++this.generation; this.abort.abort(); this.observer.disconnect(); this.image.removeAttribute('src'); this.root.remove(); }
}


/** Reject crossing edges before a polygon annotation can be saved. */
export function polygonCrossesItself(points: [number, number][]): boolean {
  const orientation = (a: [number, number], b: [number, number], c: [number, number]) => (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
  for (let i = 0; i < points.length; i++) {
    const a = points[i], b = points[(i + 1) % points.length];
    for (let j = i + 2; j < points.length; j++) {
      if (i === 0 && j === points.length - 1) continue;
      const c = points[j], d = points[(j + 1) % points.length];
      if (orientation(a, b, c) * orientation(a, b, d) < 0 && orientation(c, d, a) * orientation(c, d, b) < 0) return true;
    }
  }
  return false;
}
